import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../i18n/LanguageContext';
import banderaRD from '../images/language/RD.png';
import banderaUS from '../images/language/USA.png';

const LANGUAGES = [
    { code: 'es', label: 'Español', flag: banderaRD },
    { code: 'en', label: 'English', flag: banderaUS },
];

export default function LanguageDropdown({ className }) {
    const { language, changeLanguage } = useLanguage();
    const [open, setOpen] = useState(false);
    const buttonRef = useRef(null);
    const menuRef = useRef(null);
    const [coords, setCoords] = useState(null);

    // Close when clicking/tapping outside (consider portal menu too)
    useEffect(() => {
        function handleOutside(e) {
            if (buttonRef.current && buttonRef.current.contains(e.target)) return;
            if (menuRef.current && menuRef.current.contains(e.target)) return;
            setOpen(false);
        }
        window.addEventListener('mousedown', handleOutside);
        window.addEventListener('touchstart', handleOutside);
        return () => {
            window.removeEventListener('mousedown', handleOutside);
            window.removeEventListener('touchstart', handleOutside);
        };
    }, []);

    // Compute position when opening and on scroll/resize
    useEffect(() => {
        if (!open) return;
        const rect = buttonRef.current.getBoundingClientRect();
        const base = { top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, buttonRect: rect };
        setCoords(base);

        const handleMove = () => {
            if (!buttonRef.current) return;
            const r = buttonRef.current.getBoundingClientRect();
            setCoords({ top: r.bottom + window.scrollY, left: r.left + window.scrollX, buttonRect: r });
        };

        window.addEventListener('scroll', handleMove, true);
        window.addEventListener('resize', handleMove);
        return () => {
            window.removeEventListener('scroll', handleMove, true);
            window.removeEventListener('resize', handleMove);
        };
    }, [open]);

    // Adjust horizontal position to keep menu within viewport
    useLayoutEffect(() => {
        if (!open || !menuRef.current || !coords) return;
        const menu = menuRef.current;
        const menuRect = menu.getBoundingClientRect();
        let left = coords.left;
        const margin = 8;
        if (left + menuRect.width > window.innerWidth - margin) {
            left = Math.max(margin, coords.buttonRect.right + window.scrollX - menuRect.width);
        }
        if (left < margin) left = margin;
        if (left !== coords.left) setCoords(prev => ({ ...prev, left }));
    }, [open, coords]);

    useEffect(() => {
        if (!open || !menuRef.current) return;
        // focus first menu button for keyboard users
        const first = menuRef.current.querySelector('button[role="menuitem"]');
        if (first) first.focus();
    }, [open]);

    const current = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];

    const menu = (open && coords && typeof document !== 'undefined') ? createPortal(
        <ul
            ref={menuRef}
            role="menu"
            aria-label="Language selector"
            style={{
                position: 'absolute',
                top: `${coords.top}px`,
                left: `${coords.left}px`,
                listStyle: 'none',
                padding: 4,
                background: '#fff',
                border: '1px solid #ddd',
                borderRadius: 6,
                boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                minWidth: 160,
                zIndex: 999999,
            }}
        >
            {LANGUAGES.map((l) => (
                <li key={l.code} role="none" style={{ padding: 0 }}>
                    <button
                        role="menuitem"
                        type="button"
                        onClick={() => {
                            changeLanguage(l.code);
                            setOpen(false);
                        }}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '8px 12px',
                            background: 'none',
                            border: 'none',
                            width: '100%',
                            textAlign: 'left',
                            cursor: 'pointer',
                        }}
                    >
                        <img src={l.flag} alt={l.label} style={{ width: 24, height: 16, objectFit: 'cover', borderRadius: 2 }} />
                        <span style={{ flex: 1 }}>{l.label}</span>
                        {l.code === language && <span style={{ marginLeft: 8, color: '#007bff', fontSize: 12 }}>✓</span>}
                    </button>
                </li>
            ))}
        </ul>,
        document.body
    ) : null;

    return (
        <div className={`language-dropdown ${className || ''}`} style={{ display: 'inline-block' }}>
            <button
                ref={buttonRef}
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setOpen(true);
                    }
                    if (e.key === 'Escape') setOpen(false);
                }}
                style={{
                    background: 'none',
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    padding: '5px 10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                }}
            >
                <img src={current.flag} alt={current.label} style={{ width: 24, height: 16, objectFit: 'cover', borderRadius: 2 }} />
                <span style={{ fontWeight: 600 }}>({current.code.toUpperCase()})</span>
            </button>

            {menu}
        </div>
    );
}
